'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import FAQItemEnhanced from './FAQItemEnhanced';
import SearchBar from './SearchBar';
import CategoryFilter from './CategoryFilter';
import TrendingQuestions from './TrendingQuestions';
import LiveChatButton from './LiveChatButton';
import { useLanguage } from '@/contexts/LanguageContext';

interface FAQ {
  id: number;
  question: string;
  answer: string;
  category: string;
  relatedLinks?: { text: string; href: string }[];
  kuwaitSpecific?: boolean;
  trending?: boolean;
  helpfulVotes: number;
  notHelpfulVotes: number;
}

const FAQInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const { language, t } = useLanguage();

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const categories = [
    t({ en: 'All', ar: 'الكل' }),
    t({ en: 'Moving Process', ar: 'عملية النقل' }),
    t({ en: 'Pricing & Payments', ar: 'الأسعار والمدفوعات' }),
    t({ en: 'Insurance & Protection', ar: 'التأمين والحماية' }),
    t({ en: 'Kuwait Regulations', ar: 'اللوائح الكويتية' }),
    t({ en: 'Emergency Support', ar: 'الدعم الطارئ' })
  ];

  const faqs: FAQ[] = [
    {
      id: 1,
      question: t({ en: 'How far in advance should I book my move in Kuwait?', ar: 'كم من الوقت مسبقًا يجب أن أحجز نقلتي في الكويت؟' }),
      answer: t({ en: 'We recommend booking at least 2-3 weeks in advance, especially during peak moving seasons (September-October and March-April). For summer months (June-August), you may have more flexibility. However, last-minute bookings can often be accommodated based on availability.', ar: 'نوصي بالحجز قبل أسبوعين إلى 3 أسابيع على الأقل، خاصة خلال مواسم النقل الذروة (سبتمبر-أكتوبر ومارس-أبريل). بالنسبة لأشهر الصيف (يونيو-أغسطس)، قد تكون لديك مرونة أكبر. ومع ذلك، يمكن غالبًا استيعاب الحجوزات في اللحظة الأخيرة بناءً على التوفر.' }),
      category: t({ en: 'Moving Process', ar: 'عملية النقل' }),
      kuwaitSpecific: true,
      trending: true,
      helpfulVotes: 0,
      notHelpfulVotes: 0,
      relatedLinks: [
        { text: t({ en: 'Book Your Move', ar: 'احجز نقلتك' }), href: '/instant-quote' },
        { text: t({ en: 'View Services', ar: 'عرض الخدمات' }), href: '/services' }
      ]
    },
    {
      id: 2,
      question: t({ en: 'What are your pricing and payment options?', ar: 'ما هي خيارات التسعير والدفع لديكم؟' }),
      answer: t({ en: 'Our pricing is transparent and based on factors including distance, volume of items, special handling requirements, and timing. We offer flexible payment options including cash, credit/debit cards, and bank transfers. A deposit may be required for large moves. Get an instant quote online or contact us for a detailed estimate.', ar: 'أسعارنا شفافة وتستند إلى عوامل تشمل المسافة وحجم العناصر ومتطلبات المناولة الخاصة والتوقيت. نقدم خيارات دفع مرنة تشمل النقد وبطاقات الائتمان/الخصم والتحويلات المصرفية. قد يلزم دفع عربون للنقلات الكبيرة. احصل على عرض أسعار فوري عبر الإنترنت أو اتصل بنا للحصول على تقدير مفصل.' }),
      category: t({ en: 'Pricing & Payments', ar: 'الأسعار والمدفوعات' }),
      trending: true,
      helpfulVotes: 0,
      notHelpfulVotes: 0,
      relatedLinks: [
        { text: t({ en: 'Get Instant Quote', ar: 'احصل على عرض فوري' }), href: '/instant-quote' },
        { text: t({ en: 'Pricing Calculator', ar: 'حاسبة الأسعار' }), href: '/services' }
      ]
    },
    {
      id: 3,
      question: t({ en: 'Do you provide insurance for my belongings?', ar: 'هل تقدمون تأمينًا لممتلكاتي؟' }),
      answer: t({ en: 'Yes, we offer comprehensive insurance coverage options for your peace of mind. Our standard service includes basic liability coverage, and we offer additional full-value protection plans. We handle all items with professional care, using quality packing materials and trained staff to minimize any risk of damage.', ar: 'نعم، نقدم خيارات تغطية تأمينية شاملة لراحة بالك. تتضمن خدمتنا القياسية تغطية المسؤولية الأساسية، ونقدم خطط حماية إضافية كاملة القيمة. نتعامل مع جميع العناصر برعاية احترافية، باستخدام مواد تعبئة عالية الجودة وموظفين مدربين لتقليل أي خطر للتلف.' }),
      category: t({ en: 'Insurance & Protection', ar: 'التأمين والحماية' }),
      helpfulVotes: 0,
      notHelpfulVotes: 0,
      relatedLinks: [
        { text: t({ en: 'Learn About Insurance', ar: 'تعرف على التأمين' }), href: '/services' },
        { text: t({ en: 'Contact Us', ar: 'اتصل بنا' }), href: '/contact' }
      ]
    },
    {
      id: 4,
      question: t({ en: 'What documents do I need for moving within Kuwait?', ar: 'ما هي المستندات التي أحتاجها للنقل داخل الكويت؟' }),
      answer: t({ en: 'For local moves within Kuwait, you typically need your Civil ID and tenancy agreement (if applicable). For moves involving storage or cross-governorate relocations, additional documentation may be required. Our team will guide you through all necessary paperwork during the booking process.', ar: 'للنقل المحلي داخل الكويت، عادة ما تحتاج إلى بطاقتك المدنية وعقد الإيجار (إن وجد). للنقلات التي تتضمن التخزين أو عمليات النقل عبر المحافظات، قد تكون هناك حاجة إلى وثائق إضافية. سيرشدك فريقنا خلال جميع الأوراق اللازمة أثناء عملية الحجز.' }),
      category: t({ en: 'Kuwait Regulations', ar: 'اللوائح الكويتية' }),
      kuwaitSpecific: true,
      helpfulVotes: 0,
      notHelpfulVotes: 0,
      relatedLinks: [
        { text: 'Contact Support', href: '/contact' }
      ]
    },
    {
      id: 5,
      question: 'Can you move during Kuwait\'s summer heat?',
      answer: 'Absolutely! We operate year-round with special precautions during summer months (June-September). Our team works during cooler morning hours when possible, uses climate-controlled vehicles for sensitive items, and takes extra care with heat-sensitive belongings. We recommend scheduling early morning moves during peak summer.',
      category: 'Kuwait Regulations',
      kuwaitSpecific: true,
      trending: true,
      helpfulVotes: 0,
      notHelpfulVotes: 0,
      relatedLinks: [
        { text: 'Summer Moving Tips', href: '/resources' }
      ]
    },
    {
      id: 6,
      question: 'What if I need emergency moving services?',
      answer: 'We offer emergency and same-day moving services based on availability. Contact our 24/7 emergency hotline at 6991 1181 for urgent moving needs. Additional fees may apply for emergency services, but we\'ll always provide transparent pricing before proceeding.',
      category: 'Emergency Support',
      helpfulVotes: 0,
      notHelpfulVotes: 0,
      relatedLinks: [
        { text: 'Emergency Contact', href: '/contact' },
        { text: 'Call Now', href: 'tel:69911181' }
      ]
    },
    {
      id: 7,
      question: 'Do you provide packing materials and services?',
      answer: 'Yes! We offer complete packing services including professional packing, quality materials (boxes, bubble wrap, tape, etc.), and specialized packing for fragile items. You can choose full-service packing, partial packing, or just purchase materials from us to pack yourself.',
      category: 'Moving Process',
      helpfulVotes: 0,
      notHelpfulVotes: 0,
      relatedLinks: [
        { text: 'Packing Services', href: '/services' },
        { text: 'Packing Tips', href: '/resources' }
      ]
    },
    {
      id: 8,
      question: 'How do you handle furniture disassembly and reassembly?',
      answer: 'Our professional team handles furniture disassembly and reassembly as part of our comprehensive moving service. We carefully dismantle large furniture pieces, label all parts, transport them safely, and reassemble everything at your new location. This service is included in most moving packages.',
      category: 'Moving Process',
      helpfulVotes: 0,
      notHelpfulVotes: 0,
      relatedLinks: [
        { text: 'View Services', href: '/services' }
      ]
    },
    {
      id: 9,
      question: 'What items cannot be moved?',
      answer: 'For safety and legal reasons, we cannot transport hazardous materials (flammable liquids, gases, explosives), perishable foods, plants, pets, or illegal items. We also recommend carrying valuable documents, jewelry, and irreplaceable items personally. Contact us if you have questions about specific items.',
      category: 'Moving Process',
      helpfulVotes: 0,
      notHelpfulVotes: 0
    },
    {
      id: 10,
      question: 'Do you offer storage solutions?',
      answer: 'Yes, we provide secure short-term and long-term storage solutions in climate-controlled facilities. Perfect for situations where your new home isn\'t ready yet or you need to declutter before moving. Our storage units are monitored 24/7 and fully insured.',
      category: 'Moving Process',
      helpfulVotes: 0,
      notHelpfulVotes: 0,
      relatedLinks: [
        { text: 'Storage Services', href: '/services' }
      ]
    },
    {
      id: 11,
      question: 'What payment methods do you accept?',
      answer: 'We accept multiple payment methods for your convenience: cash (KWD), credit/debit cards (Visa, Mastercard), bank transfers, and K-Net. Payment terms will be discussed during booking, with options for deposit and final payment upon completion.',
      category: 'Pricing & Payments',
      helpfulVotes: 0,
      notHelpfulVotes: 0
    },
    {
      id: 12,
      question: 'Are there any hidden fees?',
      answer: 'No hidden fees! We believe in transparent pricing. All costs are clearly outlined in your quote, including labor, transportation, materials, and any additional services. The only additional charges would be for services requested on moving day that weren\'t in the original quote.',
      category: 'Pricing & Payments',
      trending: true,
      helpfulVotes: 0,
      notHelpfulVotes: 0,
      relatedLinks: [
        { text: 'Get Transparent Quote', href: '/instant-quote' }
      ]
    },
    {
      id: 13,
      question: 'What happens if my items are damaged during the move?',
      answer: 'While we take every precaution to prevent damage, if something does occur, our insurance coverage protects you. Report any damage immediately upon delivery, and we\'ll process your claim quickly. Our comprehensive insurance options ensure you\'re fully protected.',
      category: 'Insurance & Protection',
      helpfulVotes: 0,
      notHelpfulVotes: 0,
      relatedLinks: [
        { text: 'Insurance Details', href: '/services' },
        { text: 'File a Claim', href: '/contact' }
      ]
    },
    {
      id: 14,
      question: 'How do you protect fragile and valuable items?',
      answer: 'We use specialized packing materials (bubble wrap, foam, custom crating) for fragile items. Valuable items receive extra attention with individual wrapping and secure placement in our vehicles. Our team is trained in handling antiques, artwork, electronics, and other delicate belongings.',
      category: 'Insurance & Protection',
      helpfulVotes: 0,
      notHelpfulVotes: 0
    },
    {
      id: 15,
      question: 'Do I need special permits for moving in Kuwait?',
      answer: 'Most residential moves within Kuwait don\'t require special permits. However, some buildings or compounds may require advance notification or access permits. We\'ll help coordinate with building management to ensure smooth access on moving day.',
      category: 'Kuwait Regulations',
      kuwaitSpecific: true,
      helpfulVotes: 0,
      notHelpfulVotes: 0
    },
    {
      id: 16,
      question: 'What are the best times to move in Kuwait?',
      answer: 'The most comfortable moving periods are October-May when temperatures are moderate. Summer moves (June-September) are possible with early morning scheduling. Avoid moving during Ramadan if possible, as working hours are reduced. Weekdays typically offer more flexibility than weekends.',
      category: 'Kuwait Regulations',
      kuwaitSpecific: true,
      helpfulVotes: 0,
      notHelpfulVotes: 0,
      relatedLinks: [
        { text: 'Plan Your Move', href: '/instant-quote' }
      ]
    },
    {
      id: 17,
      question: 'Can I reschedule or cancel my move?',
      answer: 'Yes, we understand plans change. You can reschedule your move with at least 48 hours notice without penalty. Cancellations made less than 24 hours before the scheduled move may incur a cancellation fee. Contact us as soon as possible if you need to make changes.',
      category: 'Moving Process',
      helpfulVotes: 0,
      notHelpfulVotes: 0,
      relatedLinks: [
        { text: 'Contact Us', href: '/contact' }
      ]
    },
    {
      id: 18,
      question: 'How long does a typical move take?',
      answer: 'Move duration depends on several factors: size of home, amount of belongings, distance, and services required. A typical 2-bedroom apartment move takes 4-6 hours, while larger villas may take 8-12 hours. We\'ll provide a time estimate during your quote.',
      category: 'Moving Process',
      helpfulVotes: 0,
      notHelpfulVotes: 0
    }
  ];

  const filteredFAQs = faqs.filter((faq) => {
    const matchesCategory = activeCategory === 'All' || faq.category === activeCategory;
    const matchesSearch = searchQuery === '' ||
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const trendingFAQs = faqs.filter(faq => faq.trending);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.length > 2) {
      const matches = faqs
        .filter(faq => faq.question.toLowerCase().includes(query.toLowerCase()))
        .map(faq => faq.question)
        .slice(0, 5);
      setSuggestions(matches);
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setSearchQuery(suggestion);
    setSuggestions([]);
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-16">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-muted rounded w-1/3"></div>
            <div className="h-6 bg-muted rounded w-2/3"></div>
            <div className="grid grid-cols-1 gap-6">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-24 bg-muted rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary via-secondary to-brand-ocean text-white py-20 lg:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="QuestionMarkCircleIcon" size={40} variant="solid" className="text-white" />
            </div>
            <h1 className="text-4xl lg:text-6xl font-headline font-bold mb-6">
              {language === 'en' ? 'FAQ & Help Center' : 'الأسئلة الشائعة ومركز المساعدة'}
            </h1>
            <p className="text-xl lg:text-2xl text-white/90 mb-8">
              {language === 'en' ?'Find answers to common questions about moving services in Kuwait' :'اعثر على إجابات للأسئلة الشائعة حول خدمات النقل في الكويت'}
            </p>

            {/* Language Toggle */}
            <div className="flex justify-center gap-3 mb-8">
              <button
                onClick={() => setLanguage('en')}
                className={`px-6 py-2 rounded-lg font-semibold transition-all duration-base ${
                  language === 'en' ?'bg-white text-primary' :'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                English
              </button>
              <button
                onClick={() => setLanguage('ar')}
                className={`px-6 py-2 rounded-lg font-semibold transition-all duration-base ${
                  language === 'ar' ?'bg-white text-primary' :'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                العربية
              </button>
            </div>

            {/* Search Bar */}
            <SearchBar
              searchQuery={searchQuery}
              onSearchChange={handleSearch}
              suggestions={suggestions}
              onSuggestionClick={handleSuggestionClick}
              language={language}
            />
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-12 bg-card border-b border-border">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-4xl font-headline font-bold text-primary mb-2">{faqs.length}+</div>
              <div className="text-text-secondary">
                {language === 'en' ? 'FAQs Available' : 'أسئلة متاحة'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-headline font-bold text-primary mb-2">{categories.length - 1}</div>
              <div className="text-text-secondary">
                {language === 'en' ? 'Categories' : 'فئات'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-headline font-bold text-primary mb-2">24/7</div>
              <div className="text-text-secondary">
                {language === 'en' ? 'Support Available' : 'دعم متاح'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-headline font-bold text-primary mb-2">&lt;5min</div>
              <div className="text-text-secondary">
                {language === 'en' ? 'Avg Response Time' : 'متوسط وقت الاستجابة'}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trending Questions */}
      {trendingFAQs.length > 0 && (
        <TrendingQuestions faqs={trendingFAQs} language={language} />
      )}

      {/* Category Filter */}
      <CategoryFilter
        categories={categories}
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
        language={language}
      />

      {/* FAQ List */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {filteredFAQs.length > 0 ? (
              <div className="space-y-4">
                {filteredFAQs.map((faq) => (
                  <FAQItemEnhanced key={faq.id} {...faq} language={language} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <Icon
                  name="MagnifyingGlassIcon"
                  size={64}
                  variant="outline"
                  className="text-text-secondary mx-auto mb-4"
                />
                <p className="text-xl text-text-secondary">
                  {language === 'en' ?'No FAQs found matching your search' :'لم يتم العثور على أسئلة تطابق بحثك'}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Contact Support Section */}
      <section className="py-16 bg-muted">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Icon
              name="ChatBubbleLeftRightIcon"
              size={48}
              variant="outline"
              className="text-primary mx-auto mb-6"
            />
            <h2 className="text-3xl lg:text-4xl font-headline font-bold text-text-primary mb-4">
              {language === 'en' ? 'Still Need Help?' : 'لا تزال بحاجة إلى مساعدة؟'}
            </h2>
            <p className="text-lg text-text-secondary mb-8">
              {language === 'en' ?'Our support team is available 24/7 to answer your questions' :'فريق الدعم لدينا متاح على مدار الساعة للإجابة على أسئلتك'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:69911181"
                className="flex items-center justify-center space-x-2 px-8 py-4 rounded-lg bg-secondary text-secondary-foreground hover:bg-opacity-90 transition-all duration-base font-body font-semibold shadow-soft"
              >
                <Icon name="PhoneIcon" size={20} variant="solid" />
                <span className="font-accent">6991 1181</span>
              </a>
              <a
                href="/contact"
                className="flex items-center justify-center space-x-2 px-8 py-4 rounded-lg bg-primary text-primary-foreground hover:bg-opacity-90 transition-all duration-base font-body font-semibold shadow-soft"
              >
                <Icon name="EnvelopeIcon" size={20} variant="solid" />
                <span>{language === 'en' ? 'Contact Us' : 'اتصل بنا'}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Live Chat Button */}
      <LiveChatButton language={language} />
    </div>
  );
};

export default FAQInteractive;